import {
  CustomFieldDataType,
  CustomFieldDefinition as ECCFDefinition
} from '@120wateraudit/envirio-components/dist/models'
import moment from 'moment'

import { FilterDefinition, FilterType } from 'src/components/Table/types'

const BOOLEAN_FILTER_OPTIONS = [
  {
    text: 'True',
    value: true
  },
  {
    text: 'False',
    value: false
  }
]

interface CustomFieldData
  extends Omit<ECCFDefinition, 'pickListValues' | 'id'> {
  id: string
  pickListValues?: string[]
}

type CustomFieldValueProperty =
  | 'valueBit'
  | 'valueDateTime'
  | 'valueNumber'
  | 'valueText'

class CustomFieldDefinition {
  id: number
  dataType: CustomFieldDataType
  name: string
  picklistValues: string[]

  constructor(params: CustomFieldData) {
    this.id = parseInt(params.id, 10)
    this.dataType = params.dataType
    this.name = params.name
    this.picklistValues = params.pickListValues || []
  }

  public get filterType(): FilterType {
    switch (this.dataType) {
      case CustomFieldDataType.Date:
        return FilterType.DatePicker
      case CustomFieldDataType.Hyperlink:
      case CustomFieldDataType.Text:
        return FilterType.TextMatch
      case CustomFieldDataType.Number:
        return FilterType.Number
      case CustomFieldDataType['Date/Time']:
        return FilterType.DateTimePicker
      case CustomFieldDataType['Pick List']:
      case CustomFieldDataType['True/False']:
        return FilterType.ListSelection
      default:
        return FilterType.TextMatch
    }
  }

  public get filterOptions(): undefined | FilterDefinition['options'] {
    if (this.dataType === CustomFieldDataType['True/False']) {
      return BOOLEAN_FILTER_OPTIONS
    }

    if (this.dataType === CustomFieldDataType['Pick List']) {
      return this.picklistValues.map(value => ({ text: value, value }))
    }

    return undefined
  }

  public get valueProperty(): CustomFieldValueProperty {
    switch (this.dataType) {
      case CustomFieldDataType.Date:
      case CustomFieldDataType['Date/Time']:
        return 'valueDateTime'
      case CustomFieldDataType.Hyperlink:
      case CustomFieldDataType.Text:
      case CustomFieldDataType['Pick List']:
        return 'valueText'
      case CustomFieldDataType.Number:
        return 'valueNumber'
      case CustomFieldDataType['True/False']:
        return 'valueBit'
      default:
        return 'valueText'
    }
  }

  public get filterDefintion(): FilterDefinition {
    return {
      customField: this,
      key: `cf-${this.name}`,
      label: this.name,
      options: this.filterOptions,
      type: this.filterType
    }
  }

  public get defaultFilterValue() {
    switch (this.dataType) {
      case CustomFieldDataType.Date:
      case CustomFieldDataType['Date/Time']:
        return {
          [this.valueProperty]: moment().toISOString(),
          dataType: this.dataType,
          operator: 'lessThanOrEqualTo'
        }
      case CustomFieldDataType.Hyperlink:
      case CustomFieldDataType.Text:
        return {
          [this.valueProperty]: undefined,
          dataType: this.dataType,
          operator: 'like'
        }
      case CustomFieldDataType.Number:
        return {
          [`${this.valueProperty}2`]: null,
          [this.valueProperty]: '0',
          dataType: this.dataType,
          operator: 'greaterThanOrEqualTo'
        }
      case CustomFieldDataType['Pick List']:
      case CustomFieldDataType['True/False']:
      default:
        return {
          [this.valueProperty]: undefined,
          dataType: this.dataType,
          operator: 'equals'
        }
    }
  }
}

export default CustomFieldDefinition
